import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { login, register } from "@/api/auth";
import { queryKeys } from "@/api/queries";
import { Button } from "@/components/Button";
import { SegmentControl } from "@/components/SegmentControl";
import { useUiStore } from "@/store/ui";

type AuthMode = "login" | "register";

/**
 * 登录 / 注册（`specs/15` §2 · `ADR-018` D1 · `specs/07` 追加节「登录界面重设计」）。
 *
 * 暗色品牌分栏：左品牌叙事（复用首页 `.brand-*` / `.hero-network` 基元），
 * 右登录卡片（浅色 `.panel` + `.form-field`）。结构变更与取舍见 `specs/07`。
 *
 * 本页**不读服务端数据**，只提交凭证 —— 因此声明 `static`（它不是工作台视图，
 * `views/studio/**` 才禁止 `static`；见 RULES §11.3）。
 *
 * @dataSource static
 */
export function LoginView() {
  const navigate = useNavigate();
  const location = useLocation();
  const client = useQueryClient();
  const pushToast = useUiStore((state) => state.pushToast);
  // 访问控制带过来的回跳路径（`RequireAuth` 的 state.from），缺省回工作台总览。
  const from = (location.state as { from?: string } | null)?.from ?? "/studio/overview";
  const [mode, setMode] = useState<AuthMode>("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const isRegister = mode === "register";

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      const user = isRegister
        ? await register(email, password, displayName)
        : await login(email, password);
      // 会话恢复依赖 `useSession` 的缓存，登录后必须失效它，否则头部/访问控制仍是旧值。
      await client.invalidateQueries({ queryKey: queryKeys.session });
      pushToast({
        title: "已登录",
        detail: `${user.display_name || user.email} · ${user.platform_role === "editor" ? "编辑" : "玩家"}`,
        mark: "✓",
      });
      // player 无创作权限：登录后进观众侧，不进创作台（`specs/15` §2）。
      navigate(user.platform_role === "editor" ? from : "/discover", { replace: true });
    } catch (cause) {
      // 失败必须出声（RULES §11.2）：错误信封已被 client.ts 解包成可读 message。
      setError(cause instanceof Error ? cause.message : "登录失败");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="auth-view">
      <div className="auth-shell">
        <section className="auth-brand" aria-labelledby="auth-brand-title">
          <div className="hero-network" aria-hidden="true">
            <svg viewBox="0 0 1440 720" preserveAspectRatio="xMidYMid slice">
              <path
                className="network-path path-muted"
                d="M 716 108 C 820 108, 842 176, 928 176 S 1066 122, 1152 146"
              />
              <path
                className="network-path path-audience"
                d="M 716 108 C 804 108, 820 270, 916 270 S 1066 244, 1164 282"
              />
              <path
                className="network-path path-creator"
                d="M 716 108 C 786 108, 824 386, 936 386 S 1070 438, 1182 428"
              />
              <path
                className="network-path path-muted"
                d="M 928 176 C 1048 176, 1076 548, 1244 548"
              />
              <path
                className="network-path path-audience"
                d="M 916 270 C 1058 270, 1090 548, 1244 548"
              />
              <path
                className="network-path path-creator"
                d="M 936 386 C 1072 386, 1104 548, 1244 548"
              />
              <circle className="network-point point-origin" cx="716" cy="108" r="7" />
              <circle className="network-point point-muted" cx="928" cy="176" r="6" />
              <circle className="network-point point-audience" cx="916" cy="270" r="7" />
              <circle className="network-point point-creator" cx="936" cy="386" r="7" />
              <circle className="network-point point-outcome" cx="1244" cy="548" r="8" />
            </svg>
          </div>

          <div className="auth-brand-copy">
            <span className="hero-kicker">
              <i></i> 互动故事创作与生产平台
            </span>
            <h1 id="auth-brand-title">
              <span>未必</span>
              <small>Otherwise</small>
            </h1>
            <p className="brand-belief">故事不必只有一种可能。</p>
            <p className="brand-manifesto">
              把<span>分支</span>藏起来给所有人，
              <br />把<span>结构</span>展开给创作者。
            </p>
            <p className="brand-support">
              故事玩家只需要做出选择、留下脑洞；专业创作者在背后管理状态、关系、成本与可制作素材。每一次参与，都会成为下一部互动作品的真实信号。
            </p>
            <div className="hero-proof" aria-label="平台当前状态">
              <span>
                <strong>1</strong> 个真实项目验证
              </span>
              <span>
                <strong>9</strong> 个固定剧情锚点
              </span>
              <span>
                <strong>286</strong> 条观众信号
              </span>
            </div>
          </div>
        </section>

        <section className="auth-side" aria-labelledby="auth-card-title">
          <div className="panel auth-card">
            <div className="panel-heading">
              <div>
                <span className="eyebrow">账号</span>
                <h2 id="auth-card-title">{isRegister ? "创建账号" : "登录"}</h2>
              </div>
            </div>

            <div className="panel-body">
              <SegmentControl
                label="登录或注册"
                className="auth-switch"
                value={mode}
                options={[
                  { value: "login", label: "登录" },
                  { value: "register", label: "注册" },
                ]}
                onChange={(next) => {
                  setMode(next);
                  setError("");
                }}
              />

              <form className="auth-form" onSubmit={submit}>
                <div className="form-field">
                  <label htmlFor="auth-email">邮箱</label>
                  <input
                    id="auth-email"
                    type="email"
                    value={email}
                    autoComplete="email"
                    required
                    onChange={(event) => setEmail(event.target.value)}
                  />
                </div>

                {isRegister ? (
                  <div className="form-field">
                    <label htmlFor="auth-display-name">显示名</label>
                    <input
                      id="auth-display-name"
                      type="text"
                      value={displayName}
                      autoComplete="nickname"
                      onChange={(event) => setDisplayName(event.target.value)}
                    />
                  </div>
                ) : null}

                <div className="form-field">
                  <label htmlFor="auth-password">密码</label>
                  <input
                    id="auth-password"
                    type="password"
                    value={password}
                    autoComplete={isRegister ? "new-password" : "current-password"}
                    required
                    minLength={isRegister ? 8 : 1}
                    onChange={(event) => setPassword(event.target.value)}
                  />
                </div>

                {error ? (
                  <p className="form-error" role="alert">
                    {error}
                  </p>
                ) : null}

                <div className="form-actions">
                  <Button variant="primary" type="submit" className="auth-submit" disabled={busy}>
                    {busy ? "处理中…" : isRegister ? "注册并进入" : "登录并进入"}
                  </Button>
                </div>
              </form>

              <p className="inline-note">观众登录后进入故事广场；创作者进入专业创作台。</p>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
